import React from 'react';
import propTypes from 'prop-types';
import classNames from 'classnames';
import { get } from 'lodash';

// constants
import { applicationProperties } from 'constants/scripts';
import { NITROGEN_APPLICATION_RATE_TYPES } from 'constants/values';
// components
import ColorBullet from 'common/ColorBullet';
import ZoneMeta from 'edit/ZoneMeta';
import ZoneProductApplicationInputWeb from 'edit/ZoneProductApplicationInputWeb';

// util
import { convertFertilityRateForLocale } from 'util/unitConversion';
import { formatInputText } from 'util/formatters/textInputFormatter';
import { transformValidationMessage } from '../validations/utils';

const baseString = 'vra-zone-container';

let FertilityZone = ({
  fertilizerApplications,
  isCheckStrip,
  isCustom,
  isEditingDisabled,
  isSelected,
  nitrogenApplicationRateType,
  onBlurProductApplicationInput,
  onChangeProductApplicationInput,
  onClickProductApplicationInput,
  onClickZone,
  scriptType,
  scriptTypeIsNitrogen,
  transformNitrogenRateToFertilizer,
  validationResult,
  zoneArea,
  zoneDisplayNumber,
  zoneId,
  zoneIdentifierColor,
  zoneRef,
  zoneProductivity,
  isDeadZone
}) => {
  const classString = classNames({
    [`${baseString}`]: true,
    'vra-fertility-zone-container': true,
    isSelected: isSelected
  });

  const inputClassString = classNames({
    [`${baseString}__application-property--${applicationProperties.NITROGEN_RATE}`]:
      scriptTypeIsNitrogen,
    [`${baseString}__application-property--${applicationProperties.FERTILIZER_RATE}`]:
      !scriptTypeIsNitrogen
  });

  const [fertilizerApplication] = fertilizerApplications;
  const rawNitrogenRate = get(
    fertilizerApplication,
    applicationProperties.NITROGEN_RATE
  );
  const rawFertilizerRate = get(
    fertilizerApplication,
    applicationProperties.RATE
  );

  const { rate, rateKey, convertedRawFertilizerRate }
    = convertFertilityRateForLocale(
      rawNitrogenRate,
      rawFertilizerRate,
      scriptTypeIsNitrogen,
      scriptType
    );

  const fertilizerRateValidationResult = get(validationResult, `${rateKey}`);

  const formatRate = (rate) => (isNaN(rate) ? '--' : Math.round(rate));

  const renderSecondColumn = scriptTypeIsNitrogen && !isCustom;

  return (
    <tbody
      className={classString}
      data-test-id={'fertility-zone-container'}
      onClick={() => onClickZone(zoneId)}
      ref={(e) => zoneRef(e, zoneId)}
    >
      <tr className={`${baseString}__zone-properties`}>
        {/* colored dot */}
        <td className={`${baseString}__zone-identifier`}>
          <ColorBullet color={zoneIdentifierColor} />
        </td>
        <td className={`${baseString}__meta`}>
          <ZoneMeta
            isCheckStrip={isCheckStrip}
            zoneArea={zoneArea}
            zoneDisplayNumber={zoneDisplayNumber}
            zoneId={zoneId}
            zoneProductivity={zoneProductivity}
            isDeadZone={isDeadZone}
          />
        </td>
        {/* nitrogen rate if nitrogen, fertilizer rate if not nitrogen */}
        {scriptTypeIsNitrogen
        && !isCustom
        && nitrogenApplicationRateType
          === NITROGEN_APPLICATION_RATE_TYPES.fertilizer ? (
            <td
              className={`${baseString}__application-property--${applicationProperties.FERTILIZER_RATE}--n`}
            >
              {formatRate(rate)}
            </td>
          ) : (
            <td className={inputClassString}>
              <ZoneProductApplicationInputWeb
                isEditingDisabled={isEditingDisabled}
                onBlur={onBlurProductApplicationInput}
                onChange={onChangeProductApplicationInput}
                onClick={onClickProductApplicationInput}
                property={rateKey}
                validationResult={fertilizerRateValidationResult}
                value={formatInputText(rate)}
              />
            </td>
          )}
        {/* fertilizer rate (only for nitrogen scripts) */}
        {renderSecondColumn
          && (nitrogenApplicationRateType
          === NITROGEN_APPLICATION_RATE_TYPES.nitrogen ? (
              <td
                className={`${baseString}__application-property--${applicationProperties.FERTILIZER_RATE}--n`}
              >
                {formatRate(convertedRawFertilizerRate)}
              </td>
            ) : (
              <td className={inputClassString}>
                <ZoneProductApplicationInputWeb
                  isEditingDisabled={isEditingDisabled}
                  onBlur={onBlurProductApplicationInput}
                  onChange={onChangeProductApplicationInput}
                  onClick={onClickProductApplicationInput}
                  property={applicationProperties.FERTILIZER_RATE}
                  validationResult={
                  // Transform max. nitrogen validation message to max. fertilizer
                    transformValidationMessage(
                      fertilizerRateValidationResult,
                      /([\d,])+/,
                      (rate) => {
                        const withoutCommas = rate.replace(/,/g, '');
                        const nitrogenRate = Number(withoutCommas);
                        const fertilizerRate
                        = transformNitrogenRateToFertilizer(nitrogenRate);
                        return Math.round(fertilizerRate);
                      }
                    )
                  }
                  value={formatInputText(convertedRawFertilizerRate)}
                />
              </td>
            ))}
      </tr>
      <tr className={`${baseString}__help-messages`}>
        <td />
        <td colSpan="4" />
      </tr>
    </tbody>
  );
};
FertilityZone.propTypes = {
  fertilizerApplications: propTypes.array.isRequired,
  isCheckStrip: propTypes.bool,
  isCustom: propTypes.bool,
  isEditingDisabled: propTypes.bool.isRequired,
  isSelected: propTypes.bool.isRequired,
  nitrogenApplicationRateType: propTypes.string,
  onBlurProductApplicationInput: propTypes.func.isRequired,
  onChangeProductApplicationInput: propTypes.func.isRequired,
  onClickProductApplicationInput: propTypes.func.isRequired,
  onClickZone: propTypes.func,
  scriptType: propTypes.string,
  scriptTypeIsNitrogen: propTypes.bool.isRequired,
  transformNitrogenRateToFertilizer: propTypes.func,
  validationResult: propTypes.object,
  zoneArea: propTypes.object.isRequired,
  zoneDisplayNumber: propTypes.number.isRequired,
  zoneId: propTypes.string.isRequired,
  zoneIdentifierColor: propTypes.object.isRequired,
  zoneRef: propTypes.func.isRequired,
  zoneProductivity: propTypes.number,
  isDeadZone: propTypes.bool
};
export default FertilityZone;
